/**
 * BCP-47 locale tags for languages selectable in organization/user language
 * settings. Storing the full tag (not a bare ISO 639-1 code) lets us
 * distinguish regional variants, e.g. a future `en-GB` alongside `en-US`.
 */
export const SUPPORTED_LANGUAGE_CODES = ['en-US', 'ja-JP', 'es-MX', 'de-DE'] as const;

export type LanguageCode = (typeof SUPPORTED_LANGUAGE_CODES)[number];

export const DEFAULT_LANGUAGE_CODE: LanguageCode = 'en-US';

/**
 * `codes` plus the base language of each regional one (`es-MX` adds `es`), where its shared
 * translations live. The source language's base is left out: `en-US` has no `en` files.
 */
export const withBaseLanguages = (codes: readonly string[]): string[] => {
  const sourceBase = DEFAULT_LANGUAGE_CODE.split('-')[0];
  return [
    ...new Set(
      codes.flatMap((code) => {
        const base = code.split('-')[0] ?? code;
        return base === code || base === sourceBase ? [code] : [code, base];
      }),
    ),
  ];
};

/**
 * Languages whose translations are complete in every namespace. Until a language is listed it is
 * still being prepared and may be partial, with missing strings rendering in `en-US`; once listed,
 * any missing string fails `translation-completeness.spec.ts`.
 */
export const READY_LANGUAGE_CODES: readonly LanguageCode[] = [];

export const isReadyLanguage = (code: string) =>
  (READY_LANGUAGE_CODES as readonly string[]).includes(code);

export type LanguageOption = { value: LanguageCode; label: string };

export const getLanguageOptions = (locale: string): LanguageOption[] => {
  const displayNames = new Intl.DisplayNames([locale], { type: 'language' });
  return SUPPORTED_LANGUAGE_CODES.map((code) => ({
    value: code,
    // Label off the primary subtag ('en-US' -> 'English') so the dropdown
    // stays clean while only one variant per language ships; revisit to
    // region-qualified labels once a second variant (e.g. en-GB) is added.
    label: displayNames.of(code.split('-')[0] ?? code) ?? code,
  })).sort((a, b) => a.label.localeCompare(b.label, locale));
};
